/**
 * Great-circle distance between two GeoJSON-order coordinates ([lng, lat]),
 * in metres. Single shared implementation of the haversine formula - keep
 * every distance/speed calculation on tracks routed through this one
 * function rather than re-deriving it per call site.
 * @param {[number, number]} coord1 - [lng, lat]
 * @param {[number, number]} coord2 - [lng, lat]
 * @returns {number} distance in metres
 */
const haversineMeters = (coord1, coord2) => {
  const earthRadiusMeters = 6371000;
  const toRadians = value => (Number(value) * Math.PI) / 180;
  const lat1 = toRadians(coord1[1]);
  const lat2 = toRadians(coord2[1]);
  const deltaLat = lat2 - lat1;
  const deltaLon = toRadians(coord2[0] - coord1[0]);
  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) *
      Math.cos(lat2) *
      Math.sin(deltaLon / 2) *
      Math.sin(deltaLon / 2);
  return earthRadiusMeters * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const getUTM = (latitude, longitude) => {
  let zone = Math.floor(longitude / 6 + 31);

  let letter = '';
  if (latitude < -72) letter = 'C';
  else if (latitude < -64) letter = 'D';
  else if (latitude < -56) letter = 'E';
  else if (latitude < -48) letter = 'F';
  else if (latitude < -40) letter = 'G';
  else if (latitude < -32) letter = 'H';
  else if (latitude < -24) letter = 'J';
  else if (latitude < -16) letter = 'K';
  else if (latitude < -8) letter = 'L';
  else if (latitude < 0) letter = 'M';
  else if (latitude < 8) letter = 'N';
  else if (latitude < 16) letter = 'P';
  else if (latitude < 24) letter = 'Q';
  else if (latitude < 32) letter = 'R';
  else if (latitude < 40) letter = 'S';
  else if (latitude < 48) letter = 'T';
  else if (latitude < 56) letter = 'U';
  else if (latitude < 64) letter = 'V';
  else if (latitude < 72) letter = 'W';
  else letter = 'X';

  let easting =
    ((0.5 *
      Math.log(
        (1 +
          Math.cos((latitude * Math.PI) / 180) *
            Math.sin(
              (longitude * Math.PI) / 180 - ((6 * zone - 183) * Math.PI) / 180
            )) /
          (1 -
            Math.cos((latitude * Math.PI) / 180) *
              Math.sin(
                (longitude * Math.PI) / 180 - ((6 * zone - 183) * Math.PI) / 180
              ))
      ) *
      0.9996 *
      6399593.62) /
      Math.pow(
        1 +
          Math.pow(0.0820944379, 2) *
            Math.pow(Math.cos((latitude * Math.PI) / 180), 2),
        0.5
      )) *
      (1 +
        ((Math.pow(0.0820944379, 2) / 2) *
          Math.pow(
            0.5 *
              Math.log(
                (1 +
                  Math.cos((latitude * Math.PI) / 180) *
                    Math.sin(
                      (longitude * Math.PI) / 180 -
                        ((6 * zone - 183) * Math.PI) / 180
                    )) /
                  (1 -
                    Math.cos((latitude * Math.PI) / 180) *
                      Math.sin(
                        (longitude * Math.PI) / 180 -
                          ((6 * zone - 183) * Math.PI) / 180
                      ))
              ),
            2
          ) *
          Math.pow(Math.cos((latitude * Math.PI) / 180), 2)) /
          3) +
    500000;
  easting = Math.round(easting * 100) * 0.01;
  let northing =
    (((Math.atan(
      Math.tan((latitude * Math.PI) / 180) /
        Math.cos(
          (longitude * Math.PI) / 180 - ((6 * zone - 183) * Math.PI) / 180
        )
    ) -
      (latitude * Math.PI) / 180) *
      0.9996 *
      6399593.625) /
      Math.sqrt(
        1 + 0.006739496742 * Math.pow(Math.cos((latitude * Math.PI) / 180), 2)
      )) *
      (1 +
        (0.006739496742 / 2) *
          Math.pow(
            0.5 *
              Math.log(
                (1 +
                  Math.cos((latitude * Math.PI) / 180) *
                    Math.sin(
                      (longitude * Math.PI) / 180 -
                        ((6 * zone - 183) * Math.PI) / 180
                    )) /
                  (1 -
                    Math.cos((latitude * Math.PI) / 180) *
                      Math.sin(
                        (longitude * Math.PI) / 180 -
                          ((6 * zone - 183) * Math.PI) / 180
                      ))
              ),
            2
          ) *
          Math.pow(Math.cos((latitude * Math.PI) / 180), 2)) +
    0.9996 *
      6399593.625 *
      ((latitude * Math.PI) / 180 -
        0.005054622556 *
          ((latitude * Math.PI) / 180 +
            Math.sin((2 * latitude * Math.PI) / 180) / 2) +
        (4.258201531e-5 *
          (3 *
            ((latitude * Math.PI) / 180 +
              Math.sin((2 * latitude * Math.PI) / 180) / 2) +
            Math.sin((2 * latitude * Math.PI) / 180) *
              Math.pow(Math.cos((latitude * Math.PI) / 180), 2))) /
          4 -
        (1.674057895e-7 *
          ((5 *
            (3 *
              ((latitude * Math.PI) / 180 +
                Math.sin((2 * latitude * Math.PI) / 180) / 2) +
              Math.sin((2 * latitude * Math.PI) / 180) *
                Math.pow(Math.cos((latitude * Math.PI) / 180), 2))) /
            4 +
            Math.sin((2 * latitude * Math.PI) / 180) *
              Math.pow(Math.cos((latitude * Math.PI) / 180), 2) *
              Math.pow(Math.cos((latitude * Math.PI) / 180), 2))) /
          3);
  if (letter < 'N') northing = northing + 10000000;
  northing = Math.round(northing * 100) * 0.01;

  return `${zone}${letter} ${Math.floor(easting)} ${Math.floor(northing)}`;
};

function toDegreesMinutesAndSeconds(coordinate) {
  var absolute = Math.abs(coordinate);
  var degrees = Math.floor(absolute);
  var minutesNotTruncated = (absolute - degrees) * 60;
  var minutes = Math.floor(minutesNotTruncated);
  var seconds = Math.floor((minutesNotTruncated - minutes) * 60);

  return degrees + '° ' + minutes + "' " + seconds + '"';
}

const getDegMinSec = (lat, lng) => {
  var latitude = toDegreesMinutesAndSeconds(lat);
  var latitudeCardinal = lat >= 0 ? 'N' : 'S';

  var longitude = toDegreesMinutesAndSeconds(lng);
  var longitudeCardinal = lng >= 0 ? 'E' : 'W';

  return (
    latitude +
    ' ' +
    latitudeCardinal +
    '\n' +
    longitude +
    ' ' +
    longitudeCardinal
  );
};

const getWGS = coor => {
  return coor[0] + ', ' + coor[1];
};

/**
 * Human-readable distance for a length in metres: whole metres below 1000 m,
 * kilometres with two decimals from 1000 m up (decimal comma), e.g. "850 m",
 * "1,35 km". Single shared formatter - use it wherever a measured distance
 * is shown rather than formatting per call site.
 * @param {number} meters
 * @returns {string}
 */
const formatDistanceMeters = meters => {
  const value = Number(meters);
  if (!Number.isFinite(value)) return '';
  const rounded = Math.round(value);
  if (rounded >= 1000) {
    return `${(value / 1000).toFixed(2).replace('.', ',')} km`;
  }
  return `${rounded} m`;
};

export { getUTM, getDegMinSec, getWGS, haversineMeters, formatDistanceMeters };
